import { Drawer } from '@chronosphereio/chrono-ui';
import { useState } from 'react';
import { useAnalytics } from '@/plugin-system';
import { DASHBOARD_TOOLTIP_TEXT } from '../../constants/user-interface-text';
import { PanelGroupId, useCleanUpLayoutDrawer, usePanelGroup, usePanelGroupActions } from '../../context';
import { CleanLayoutOptions, LayoutPreset } from './CleanLayoutOptions';
import { useBuildLayoutPreset } from './use-build-layout-preset';

const DEFAULT_PANEL_HEIGHT = 6;
const PANEL_HEIGHT_MIN = 6;
const PANEL_HEIGHT_MAX = 12;
/** Must match rowHeight in GridLayout.tsx so preview reflects actual panel height. */
const GRID_ROW_HEIGHT_PX = 30;

function CleanUpLayoutDrawerContent({ panelGroupId }: { panelGroupId: PanelGroupId }) {
  const group = usePanelGroup(panelGroupId);
  const { updatePanelGroupLayouts } = usePanelGroupActions(panelGroupId);
  const { closeCleanUpLayout } = useCleanUpLayoutDrawer();
  const [panelHeight, setPanelHeight] = useState(DEFAULT_PANEL_HEIGHT);
  const [selectedLayout, setSelectedLayout] = useState<LayoutPreset>('keep-existing');
  const buildLayout = useBuildLayoutPreset(group.kind === 'Grid' ? group : undefined);
  const analytics = useAnalytics();

  if (group.kind !== 'Grid') {
    return null;
  }

  const handleApply = () => {
    const updated = buildLayout(selectedLayout, panelHeight);
    if (updated) {
      updatePanelGroupLayouts(updated);
      closeCleanUpLayout();
      analytics?.track('dashboard-clean_up_layout_applied', { layout: selectedLayout });
    }
  };

  return (
    <Drawer open onClose={closeCleanUpLayout} size="small">
      <Drawer.Header
        variant="edit"
        title={DASHBOARD_TOOLTIP_TEXT.cleanUpLayout}
        primaryButtonText="Apply"
        onPrimaryButtonClick={handleApply}
        onSecondaryButtonClick={closeCleanUpLayout}
      />
      <Drawer.Content>
        <CleanLayoutOptions
          selectedLayout={selectedLayout}
          onSelectedLayoutChange={setSelectedLayout}
          panelHeight={panelHeight}
          onPanelHeightChange={setPanelHeight}
          panelHeightMin={PANEL_HEIGHT_MIN}
          panelHeightMax={PANEL_HEIGHT_MAX}
          gridRowHeightPx={GRID_ROW_HEIGHT_PX}
        />
      </Drawer.Content>
    </Drawer>
  );
}

/**
 * Drawer for cleaning up the layout of a panel group. Open/close state is controlled by the DashboardStore.
 */
export function CleanUpLayoutDrawer() {
  const { isCleanUpLayoutDrawerOpen, cleanUpLayoutPanelGroupId } = useCleanUpLayoutDrawer();

  if (!isCleanUpLayoutDrawerOpen || cleanUpLayoutPanelGroupId === undefined) {
    return null;
  }

  return <CleanUpLayoutDrawerContent panelGroupId={cleanUpLayoutPanelGroupId} />;
}
